import { gql } from '@apollo/client';
import each from 'lodash/each';
import constants from 'src/constants/globalConstants';
import BaseService from './baseService';

const { GQL_FETCH_CREATE_DISPATCH_RESOURCES_TIMEOUT } = constants.TIMEOUTS;

class DispatchWorkflowService extends BaseService {
  constructor() {
    super();
    this.dispatchWorkflowDataMappings = {
      workflowId: {
        name: '$workflowId',
        type: 'Int',
        typeRequire: 'Int!',
        input: 'workflowId'
      },
      workflowName: {
        name: '$workflowName',
        type: 'String',
        typeRequire: 'String!',
        input: 'workflowName'
      },
      workflowTypeId: {
        name: '$workflowTypeId',
        type: 'Int',
        typeRequire: 'Int!',
        input: 'workflowTypeId'
      },
      isDefaultWorkflow: {
        name: '$isDefaultWorkflow',
        type: 'Int',
        typeRequire: 'Int!',
        input: 'isDefaultWorkflow'
      },
      workflowStatus: {
        name: '$workflowStatus',
        type: 'String',
        typeRequire: 'String!',
        input: 'workflowStatus'
      },
      workflowClients: {
        name: '$workflowClients',
        type: '[JSON]',
        input: 'workflowClients'
      },
      workflowSteps: {
        name: '$workflowSteps',
        type: '[JSON!]',
        input: 'workflowSteps'
      }
    };
    this.dispatchWorkflowGql = `
      workflowId
      workflowTypeId
      workflowName
      isDefaultWorkflow
      workflowStatus
      workflowClients{
        companyId
        telephoneNumberId
        telephoneNumber
        companyToTelephoneNumberId
      }
      workflowSteps {
        workflowStepsId
        workflowOrder
        config
        script
        workflowId
        workflowComponentId
        workflowComponent {
          workflowComponentId
          componentName
          shortDescription
          description
        }
      }
    `;
    this.dispatchWorkflowComponentGql = `
      workflowComponentId
      componentName
      shortDescription
      description
    `;
    this.dispatchWorkflowTypeGql = `
      workflowTypeId
      workflowType
    `;
    this.updateDispatchWorkflow = `
      workflowName: String!
      workflowTypeId: Int!
      isDefaultWorkflow: Int!
      workflowStatus: String!
      workflowClients: [JSON]
      workflowSteps: [JSON!]
    `;

    this.dispatchWorkflowActionGql = `
      success
      token`;
  }

  buildDispatchWorkflowGQL = (data, mutation, required, actionReturned) => {
    const keys = Object.keys(data);
    const client = [];
    const input = [];
    each(keys, (key) => {
      const map = this.dispatchWorkflowDataMappings[key];
      if (map && data[key] !== null && data[key] !== undefined) {
        client.push(
          `${map.name}: ${required && map.typeRequire ? map.typeRequire : map.type
          }`
        );
        input.push(`${map.input}: ${map.name}`);
      }
    });

    return `
      mutation EditWorkflow(${client.join(', ')}){
        ${mutation}(
            ${input.join('\n')}
          ) {
            ${actionReturned ? this.dispatchWorkflowActionGql : this.dispatchWorkflowGql}
        }
    }`;
  };

  getDispatchWorkflows = () => new Promise((resolve, reject) => {
    const DISPATCH_WORKFLOWS = gql`
      query {
        dispatchWorkflows{
            ${this.dispatchWorkflowGql}
        }
      }
    `;
    const query = this.client.watchQuery({
      fetchPolicy: 'cache-first',
      query: DISPATCH_WORKFLOWS
    });
    const observable = query.subscribe(({ data }) => {
      if (data?.dispatchWorkflows) {
        resolve(data?.dispatchWorkflows);
      } else {
        reject(data?.error);
      }
    });
    setTimeout(() => {
      observable.unsubscribe();
      reject(Error('Fetching workflows timed out, retrying request', {
        cause: 'timeout'
      }));
    }, GQL_FETCH_CREATE_DISPATCH_RESOURCES_TIMEOUT);
  });

  getDispatchWorkflow = (dispatchWorkflowId) => new Promise((resolve, reject) => {
    const DISPATCH_WORKFLOWS = gql`
      query {
        dispatchWorkflow(workflowId:${dispatchWorkflowId}){
            ${this.dispatchWorkflowGql}
        }
      }
    `;

    this.client
      .query({
        fetchPolicy: 'no-cache',
        query: DISPATCH_WORKFLOWS
      })
      .then((result) => {
        if (result.data.dispatchWorkflow) {
          resolve(result.data.dispatchWorkflow);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  getDefaultDispatchWorkflow = () => new Promise((resolve, reject) => {
    const DISPATCH_WORKFLOWS = gql`
      query {
        dispatchWorkflow(DefaultWorkflow:1){
            ${this.dispatchWorkflowGql}
        }
      }
    `;

    this.client
      .query({
        fetchPolicy: 'no-cache',
        query: DISPATCH_WORKFLOWS
      })
      .then((result) => {
        if (result.data.dispatchWorkflow) {
          resolve(result.data.dispatchWorkflow);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  getDispatchWorkflowTypes = () => new Promise((resolve, reject) => {
    const TYPES = gql`
      query {
        dispatchWorkflowTypes{
            ${this.dispatchWorkflowTypeGql}
        }
      }
    `;

    this.client
      .query({
        fetchPolicy: 'no-cache',
        query: TYPES
      })
      .then((result) => {
        if (result.data.dispatchWorkflowTypes) {
          resolve(result.data.dispatchWorkflowTypes);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  addWorkflow = (workflow) => new Promise((resolve, reject) => {
    const workflowStatus = workflow.workflowStatus
      ? workflow.workflowStatus
      : 'Active';
    const WORKFLOW = gql`
      mutation DispatchWorkflow($workflowName: String!,
        $workflowTypeId: Int!,
        $isDefaultWorkflow: Int!,
        $workflowClients: [JSON],
        $workflowStatus: String!,
        $workflowSteps: [JSON!]){
        addDispatchWorkflow(
          workflowName: $workflowName
          workflowTypeId: $workflowTypeId
          isDefaultWorkflow: $isDefaultWorkflow
          workflowStatus: $workflowStatus
          workflowClients: $workflowClients
          workflowSteps: $workflowSteps
        ){
          ${this.dispatchWorkflowGql}
        }
      }
    `;

    this.client
      .mutate({
        mutation: WORKFLOW,
        variables: {
          ...workflow,
          workflowStatus
        }
      })
      .then((result) => {
        if (result.data.addDispatchWorkflow) {
          resolve(result.data.addDispatchWorkflow);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  saveWorkflow = (workflow) => new Promise((resolve, reject) => {
    const DISPATCH_WORKFLOW = gql`
        ${this.buildDispatchWorkflowGQL(
    workflow,
    'updateDispatchWorkflow',
    true,
    true
  )}
      `;

    this.client
      .mutate({
        mutation: DISPATCH_WORKFLOW,
        variables: workflow
      })
      .then((result) => {
        if (result.data.updateDispatchWorkflow) {
          resolve(result.data.updateDispatchWorkflow);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  getDispatchWorkflowComponents = () => new Promise((resolve, reject) => {
    const COMPONENTS = gql`
      query {
        dispatchWorkflowComponents{
            ${this.dispatchWorkflowComponentGql}
        }
      }
    `;

    this.client
      .query({
        fetchPolicy: 'no-cache',
        query: COMPONENTS
      })
      .then((result) => {
        if (result.data.dispatchWorkflowComponents) {
          resolve(result.data.dispatchWorkflowComponents);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  // FIXME: (jh) I currently have no clue how to make this work
  // TODO: Build out
  bulkUpdateDispatchDefaultWorkflows = (variables) => new Promise((resolve, reject) => {
    const WORKFLOW = gql`
     mutation DispatchWorkflow(
        $workflowIds: [Int!],
        $isDefault: Boolean
      ) {
      bulkUpdateDispatchDefaultWorkflows(
        workflowIds: $workflowIds
        isDefault: $isDefault
      ){
        ${this.dispatchWorkflowGql}
      }
      }`;

    this.client
      .mutate({
        mutation: WORKFLOW,
        variables
      })
      .then((result) => {
        if (result.data.bulkUpdateDispatchDefaultWorkflows) {
          resolve(result.data.bulkUpdateDispatchDefaultWorkflows);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });
}

const dispatchWorkflowService = new DispatchWorkflowService();

export default dispatchWorkflowService;
